import { useContext, useState } from "react";
import { useFormState, useWatch } from "react-hook-form";
import useRetainersDashboardNotes, {
    useRetainersNoteTypes,
} from "features/projects/retainers-dashboard/single/useRetainersDashboardNotes";

import Dropdown from "shared/components/inputs/form-fields/Dropdown";
import Form from "shared/components/inputs/form/Form";
import FormFeedbackNotificationText from "shared/components/feedback/error/FormFeedbackNotificationText";
import ModalActions from "shared/components/feedback/modals/ModalActions";
import PropTypes from "prop-types";
import RetainersDashboardLayoutContext from "features/projects/contexts/RetainersDashboardLayoutContext";
import TextArea from "shared/components/inputs/form-fields/TextArea";
import VStack from "shared/components/layout/stack/VStack";
import { getDropdownOptionsNoDefaultVal } from "shared/helper-functions/dropdown/dropdownHelpers";
import { sendNotification } from "shared/components/feedback/notification/notification";
import { useParams } from "react-router-dom";
import useRetainers from "features/admin/retainers/useRetainers";

const ModalCTA = ({
    isEditing,
    handleOnClickDeleteNote,
    isLoading,
    onClose,
}) => {
    const { isValid } = useFormState();
    const formData = useWatch();

    return (
        <ModalActions
            primaryAction={{
                type: "submit",
                label: isEditing ? "Save" : "Add Note",
                leadingIcon: isEditing ? "save" : null,
                disabled: !isValid,
            }}
            secondaryAction={{
                type: "button",
                label: "Cancel",
                onClick: onClose,
            }}
            {...(isEditing && {
                tertiaryAction: {
                    type: "button",
                    label: "Delete",
                    onClick: () => handleOnClickDeleteNote(formData),
                },
            })}
            as="fieldset"
            disabled={isLoading}
        />
    );
};

function transformRetainersNoteDataToFormData(noteData = {}) {
    return {
        noteType: noteData.TypeId,
        note: noteData.Text,
    };
}

function transformFormDataToRetainersNoteData(
    formData = {},
    RetainersNoteData = {}
) {
    const newRetainersNoteData = { ...RetainersNoteData };
    newRetainersNoteData.TypeId = formData.noteType;
    newRetainersNoteData.Content = formData.note;

    return newRetainersNoteData;
}

const RetainersDashboardNotesForm = ({ noteIdToEdit, onClose }) => {
    const {
        useRetainersNotesList,
        createRetainerDashboardNote,
        updateRetainerDashboardNote,
        deleteRetainerDashboardNote,
    } = useRetainersDashboardNotes();
    const { retainerId } = useParams();
    const { setFlagToLoad } = useContext(RetainersDashboardLayoutContext);
    const notesList = useRetainersNotesList({
        retainerIdFE: retainerId,
        noteTypeId: "",
    });
    const { retainersNoteTypes } = useRetainersNoteTypes();
    const [inProgress, setInProgress] = useState(false);

    const currentRetainersNoteData = notesList?.data?.find(
        (note) => note.Id === noteIdToEdit
    );

    const { useRetainerById } = useRetainers();
    const retainerData = useRetainerById(retainerId);

    const isEditing = noteIdToEdit !== undefined;
    const modifier = isEditing
        ? updateRetainerDashboardNote
        : createRetainerDashboardNote;

    if (isEditing && !currentRetainersNoteData) {
        return <>Could not find note</>;
    }

    const getNameOfNotification = (res) => {
        return res ? `A note on ${retainerData?.data?.RetainerName}` : "note";
    };

    const handleOnClickDeleteNote = async (prevData) => {
        const response = await deleteRetainerDashboardNote.mutateAsync({ noteId: noteIdToEdit });
        onClose();
        sendNotification(
            <FormFeedbackNotificationText
                responseStatus={response?.status}
                isDeleting={true}
                name={getNameOfNotification(response?.status === 200)}
            />,
            {
                close: true,
                action: response?.status === 200,
                actionProps: response?.status === 200
                    ? {
                        label: "Undo",
                        onClick: () =>
                            onSubmit({
                                ...prevData,
                                isNoteRemoved: true,
                            }),
                    }
                    : null,
            }
        );
    };

    const onSubmit = async (formData) => {
        const data = transformFormDataToRetainersNoteData(
            formData,
            currentRetainersNoteData
        );
        const newModifier = formData.isNoteRemoved
            ? createRetainerDashboardNote
            : modifier;
        const allData = {
            noteId: noteIdToEdit,
            body: data,
        };
        const response = await newModifier.mutateAsync(allData);
        onClose();
        setInProgress(false);
        isEditing && setFlagToLoad(true);
        sendNotification(
            <FormFeedbackNotificationText
                responseStatus={response?.status}
                isEditing={formData.isNoteRemoved ? false : isEditing}
                name={getNameOfNotification(response?.status === 200)}
            />
        );
    };

    const noteTypesOptions = retainersNoteTypes.isLoading
        ? []
        : getDropdownOptionsNoDefaultVal(retainersNoteTypes?.data);

    if (notesList.isLoading || retainersNoteTypes.isLoading) {
        return <>Loading...</>;
    }

    if (
        notesList.isError ||
        retainersNoteTypes.isError ||
        retainerData.isError
    ) {
        return <>Error</>;
    }

    return (
        <Form
            onSubmit={onSubmit}
            defaultValues={transformRetainersNoteDataToFormData(
                currentRetainersNoteData
            )}
            mode="onChange"
        >
            <VStack spacing="Three">
                <VStack>
                    <Dropdown
                        name="noteType"
                        validations={{
                            required: true,
                        }}
                        label="Note Type"
                        itemsAreLoading={retainersNoteTypes.isLoading}
                        options={noteTypesOptions}
                        maxHeightOfOptionsList={100}
                    />
                    <TextArea
                        name="note"
                        showClearIcon={true}
                        characterCounter={true}
                        label="Note"
                        placeholder="Add a note to the Retainer"
                        helpText="Character limit: 256"
                        validations={{
                            required: true,
                            maxLength: {
                                value: 256,
                                message: "Please use fewer characters",
                            },
                        }}
                    />
                </VStack>

                <ModalCTA
                    isEditing={isEditing}
                    handleOnClickDeleteNote={handleOnClickDeleteNote}
                    onClose={onClose}
                    isLoading={modifier?.isLoading || inProgress}
                />
            </VStack>
        </Form>
    );
};

ModalCTA.propTypes = {
    isEditing: PropTypes.bool,
    handleOnClickDeleteNote: PropTypes.func,
    isLoading: PropTypes.bool,
    onClose: PropTypes.func.isRequired
};

RetainersDashboardNotesForm.propTypes = {
    noteIdToEdit: PropTypes.string,
    onClose: PropTypes.func.isRequired
};

export default RetainersDashboardNotesForm;
